import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import type { TokenPair } from "@/types";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

// Separate localStorage keys from the public frontend's, and a separate
// origin (different port/subdomain) — an admin session never shares storage
// with the public site, so a compromise of one doesn't hand over the other.
const ACCESS_KEY = "maayad_admin_access";
const REFRESH_KEY = "maayad_admin_refresh";

export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set({ access_token, refresh_token }: TokenPair) {
    localStorage.setItem(ACCESS_KEY, access_token);
    localStorage.setItem(REFRESH_KEY, refresh_token);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

export const api = axios.create({
  baseURL: `${BASE_URL}/api/v1`,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.access;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// --- Refresh handling -----------------------------------------------------
let isRefreshing = false;
let queue: ((token: string | null) => void)[] = [];

function flushQueue(token: string | null) {
  queue.forEach((cb) => cb(token));
  queue = [];
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as AxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;
    const isAuthCall = original?.url?.includes("/auth/");

    if (status !== 401 || !original || original._retry || isAuthCall) {
      return Promise.reject(error);
    }

    const refresh = tokenStore.refresh;
    if (!refresh) {
      tokenStore.clear();
      return Promise.reject(error);
    }

    original._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        queue.push((token) => {
          if (!token) return reject(error);
          original.headers = original.headers ?? {};
          (original.headers as Record<string, string>).Authorization =
            `Bearer ${token}`;
          resolve(api(original));
        });
      });
    }

    isRefreshing = true;
    try {
      const { data } = await axios.post<TokenPair>(
        `${BASE_URL}/api/v1/auth/refresh`,
        { refresh_token: refresh },
      );
      tokenStore.set(data);
      flushQueue(data.access_token);
      original.headers = original.headers ?? {};
      (original.headers as Record<string, string>).Authorization =
        `Bearer ${data.access_token}`;
      return api(original);
    } catch (refreshError) {
      flushQueue(null);
      tokenStore.clear();
      if (typeof window !== "undefined") window.location.assign("/login");
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

/** Extract a human-readable message from an Axios error. */
export function errorMessage(err: unknown, fallback = "Something went wrong"): string {
  if (axios.isAxiosError(err)) {
    if (!err.response) {
      return "Can't reach the server. Make sure the backend is running at http://localhost:8000.";
    }
    const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length) {
      const first = detail[0] as { msg?: string };
      if (first?.msg) return first.msg;
    }
  }
  return fallback;
}
